import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Users, Calendar, Plus, Search, Lock, Shield } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { CreateChallengeDialog } from "./CreateChallengeDialog";
import { JoinPrivateChallenge } from "./JoinPrivateChallenge";
import { TeamSelector } from "./TeamSelector";

interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  start_date: string;
  end_date: string;
  goal_criteria: any;
  is_public: boolean;
  is_private: boolean;
  require_proof: boolean;
  max_participants: number;
  created_by: string;
  participant_count?: number;
  is_joined?: boolean;
  has_teams?: boolean;
}

interface BrowseChallengesProps {
  filterCategory?: string;
}

export function BrowseChallenges({ filterCategory }: BrowseChallengesProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(filterCategory || "all");
  const [teamSelectorOpen, setTeamSelectorOpen] = useState(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: challenges, isLoading } = useQuery({
    queryKey: ['browse-challenges', user?.id, typeFilter],
    queryFn: async () => {
      if (!user?.id) return [];

      let query = supabase
        .from('group_challenges')
        .select('*, participant_count:challenge_participants(count)')
        .eq('is_public', true)
        .gte('end_date', new Date().toISOString().split('T')[0]);

      if (typeFilter && typeFilter !== 'all') {
        query = query.eq('challenge_type', typeFilter);
      }

      const { data, error } = await query.order('start_date', { ascending: false });

      if (error) throw error;

      // Check which challenges user has joined
      const { data: userChallenges } = await supabase
        .from('challenge_participants')
        .select('challenge_id')
        .eq('user_id', user.id);

      const joinedIds = new Set(userChallenges?.map(c => c.challenge_id) || []);

      // Check which challenges have teams
      const { data: teamData } = await supabase
        .from('challenge_teams')
        .select('challenge_id');

      const teamChallengeIds = new Set(teamData?.map(t => t.challenge_id) || []);

      return data.map(challenge => ({
        ...challenge,
        participant_count: challenge.participant_count?.[0]?.count || 0,
        is_joined: joinedIds.has(challenge.id),
        has_teams: teamChallengeIds.has(challenge.id),
      })) as Challenge[];
    },
    enabled: !!user?.id,
  });

  const joinMutation = useMutation({
    mutationFn: async (challengeId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challengeId,
          user_id: user.id,
          completion_status: 'active',
        });

      if (error) throw error;
      return challengeId;
    },
    onSuccess: (challengeId) => {
      queryClient.invalidateQueries({ queryKey: ['browse-challenges'] });
      queryClient.invalidateQueries({ queryKey: ['active-challenges'] });
      
      // Check if challenge has teams
      const challenge = challenges?.find(c => c.id === challengeId);
      if (challenge?.has_teams) {
        setSelectedChallengeId(challengeId);
        setTeamSelectorOpen(true);
      } else {
        toast.success("Joined challenge!");
      }
    },
  });

  const filteredChallenges = challenges?.filter(challenge =>
    challenge.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    challenge.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading challenges...</p>
        </CardContent>
      </Card>
    );
  }

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Find a Challenge</CardTitle>
            <div className="flex gap-2">
              <JoinPrivateChallenge />
              <CreateChallengeDialog />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search challenges..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="sleep">Sleep</SelectItem>
                <SelectItem value="medication">Medication</SelectItem>
                <SelectItem value="nutrition">Nutrition</SelectItem>
                <SelectItem value="goals">Goals</SelectItem>
                <SelectItem value="meditation">Meditation</SelectItem>
                <SelectItem value="breathwork">Breathwork</SelectItem>
                <SelectItem value="habits">Habits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!filteredChallenges || filteredChallenges.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-3">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                No challenges found. Try adjusting your filters or create your own!
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredChallenges.map((challenge) => (
            <Card key={challenge.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">{challenge.title}</CardTitle>
                      <Badge variant="secondary" className="capitalize">
                        {challenge.challenge_type}
                      </Badge>
                      {challenge.is_joined && (
                        <Badge variant="default">Joined</Badge>
                      )}
                      {challenge.is_private && (
                        <Badge variant="outline" className="gap-1">
                          <Lock className="h-3 w-3" />
                          Private
                        </Badge>
                      )}
                      {challenge.has_teams && (
                        <Badge variant="outline" className="gap-1">
                          <Users className="h-3 w-3" />
                          Teams
                        </Badge>
                      )}
                      {challenge.require_proof && (
                        <Badge variant="outline" className="gap-1">
                          <Shield className="h-3 w-3" />
                          Proof Required
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{challenge.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Days Left</p>
                      <p className="font-medium">{getDaysRemaining(challenge.end_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Participants</p>
                      <p className="font-medium">{challenge.participant_count}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Goal</p>
                      <p className="font-medium text-xs">{challenge.goal_criteria?.description || 'Complete'}</p>
                    </div>
                  </div>
                </div>

                {!challenge.is_joined ? (
                  <Button
                    onClick={() => joinMutation.mutate(challenge.id)}
                    disabled={joinMutation.isPending}
                    className="w-full gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Join Challenge
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" disabled>
                    Already Joined
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Team Selector Dialog */}
      {selectedChallengeId && (
        <TeamSelector
          challengeId={selectedChallengeId}
          open={teamSelectorOpen}
          onClose={() => {
            setTeamSelectorOpen(false);
            setSelectedChallengeId(null);
          }}
        />
      )}
    </div>
  );
}
